import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { LetterService } from "./letter.service";
import { FeedbackService } from "../shared/services/feedback.service";
import { ILetterType } from "../shared/models/interfaces/letter-type";
import { NgForm } from '@angular/forms';
import { LetterTemplate } from "../shared/models/letter-template.model";
import { takeUntil, catchError, tap } from 'rxjs/operators';
import { Subject, of, forkJoin } from 'rxjs';

@Component({
  selector: 'app-letter-template',
  templateUrl: './letter-template.component.html',
  styleUrls: ['./letter-template.component.scss'],
  providers: [LetterService, FeedbackService]
})
export class LetterTemplateComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  isLoading: boolean;
  previewMode: boolean;
  letterTypes: ILetterType[];
  letterTemplate: LetterTemplate;
  letterTypeId: number;
  @ViewChild('f', { static: true }) form: NgForm;

  constructor(
    private letterService: LetterService,
    private feedbackService: FeedbackService) { }

  ngOnInit() {
    this.isLoading = true;
    this.previewMode = false;

    this.letterService.getLetterTypes().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (!response) {
          this.isLoading = false;
          this.feedbackService.error('No letter types available');
          return;
        }
        this.letterTypes = response;
        this.letterTypeId = response[0].letterTypeId;
        this.loadLetterTemplate();
      },
      error: (error) => {
        this.feedbackService.error(error);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  letterTypeChanged() {
    this.loadLetterTemplate();
  }

  private loadLetterTemplate() {
    this.isLoading = true;
    this.letterService.getLetterTemplateByLetterTypeId(this.letterTypeId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (response) {
          this.letterTemplate = response;
        } else {
          this.letterTemplate = new LetterTemplate(this.letterTypeId);
        }
      },
      error: (error) => {
        this.feedbackService.error(error);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  save() {
    if (!this.letterTemplate.letterTemplateId || this.letterTemplate.letterTemplateId === 0) {

      this.letterService
        .createLetterTemplate(this.letterTemplate)
        .pipe(
          tap((data) => {
            this.letterTemplate = data;
            this.feedbackService.success(`Letter template created.`);
          }),
          catchError((error) => {
            this.feedbackService.error(
              `There was a problem saving the Letter template.`
            );
            console.error(error);
            return of(null);
          })
        )
        .subscribe();
    } else {
      this.letterService.update(this.letterTemplate).pipe(
        tap((data) => {
          this.letterTemplate = data;
          this.feedbackService.success("Letter template updated");
        }),
        catchError((error) => {
          this.feedbackService.error("An error occurred while updating the Letter template");
          console.error('Error updating Letter template:', error);
          return of(null);
        })
      ).subscribe();
    }
  }

  isSaveDisabled() {
    return !this.form.valid;
  }

  preview() {
    this.previewMode = true;
  }

  back() {
    this.previewMode = false;
  }

  get letterTypeName(): string {
    if (this.letterTypes && this.letterTypeId) {
      const letterType = this.letterTypes.find(s => s.letterTypeId === this.letterTypeId);
      if (letterType) {
        return letterType.letterTypeName;
      }
    }

    return '';
  }

  get getDate(): Date {
    return new Date();
  }

  get getFormattedAddress(): string {
    let address: string = '';
    if (this.letterTemplate.address) {
      this.letterTemplate.address.split(',').forEach(
        line => {
          address += line + '<br/>';
        });
    }

    return address;
  }

  get getFormattedLetterBody(): string {
    let letterBody: string = '';
    if (this.letterTemplate.content) {
      this.letterTemplate.content.split('\n').forEach(
        line => {
          letterBody += line + '</p><p>';
        });
    }
    return letterBody;
  }
}
